import { useEffect, useState } from 'react';

import { api } from '../../services/api/client';
import {
  readPushDebugEvents,
  subscribeToPushDebugEvents,
  type PushDebugEvent,
} from '../../services/pwa/push-debug';

export function PushDebugPanel({ serverNotificationsEnabled }: PushDebugPanelProps) {
  const [events, setEvents] = useState<PushDebugEvent[]>([]);
  const [clientState, setClientState] = useState<ClientPushState | null>(null);
  const [error, setError] = useState('');
  const [testStatus, setTestStatus] = useState('');
  const [testing, setTesting] = useState(false);

  async function refresh() {
    setError('');
    try {
      const [storedEvents, state] = await Promise.all([
        readPushDebugEvents(),
        readClientPushState(),
      ]);
      setEvents(storedEvents);
      setClientState(state);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Die Push-Diagnose konnte nicht gelesen werden.',
      );
    }
  }

  async function sendTestPush() {
    setTesting(true);
    setError('');
    setTestStatus('Push wird an dieses Gerät gesendet …');
    try {
      const result = await api.pushTest();
      const statuses = result.results.map((item) => item.httpStatus ?? 'Fehler').join(', ');
      setTestStatus(
        result.subscriptionCount === 0
          ? 'Keine aktive Subscription auf dem Server gefunden.'
          : 'Push-Dienst antwortete mit: ' + statuses,
      );
      await refresh();
    } catch (caught) {
      setTestStatus('');
      setError(caught instanceof Error ? caught.message : 'Der Push-Test ist fehlgeschlagen.');
    } finally {
      setTesting(false);
    }
  }

  useEffect(() => {
    let mounted = true;
    void Promise.all([readPushDebugEvents(), readClientPushState()])
      .then(([storedEvents, state]) => {
        if (!mounted) return;
        setEvents(storedEvents);
        setClientState(state);
      })
      .catch((caught: unknown) => {
        if (!mounted) return;
        setError(
          caught instanceof Error
            ? caught.message
            : 'Die Push-Diagnose konnte nicht gelesen werden.',
        );
      });
    const unsubscribe = subscribeToPushDebugEvents((event) => {
      setEvents((current) => [event, ...current.filter((candidate) => candidate.id !== event.id)]);
      void readClientPushState()
        .then(setClientState)
        .catch(() => undefined);
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  return (
    <section className="push-debug-panel" aria-labelledby="push-debug-title">
      <div className="push-debug-panel__header">
        <div>
          <p className="step-label">Nur Jakob-Debug</p>
          <h3 id="push-debug-title">Push-Diagnose</h3>
        </div>
        <div className="push-debug-panel__actions">
          <button
            className="button button--secondary"
            type="button"
            disabled={testing}
            onClick={() => void sendTestPush()}
          >
            Push an dieses Gerät
          </button>
          <button className="button button--secondary" type="button" onClick={() => void refresh()}>
            Aktualisieren
          </button>
        </div>
      </div>
      <dl className="push-debug-state">
        <div>
          <dt>Server-Schalter</dt>
          <dd>{serverNotificationsEnabled ? 'aktiviert' : 'deaktiviert'}</dd>
        </div>
        <div>
          <dt>Berechtigung</dt>
          <dd>{clientState?.permission ?? 'wird geprüft …'}</dd>
        </div>
        <div>
          <dt>Service Worker</dt>
          <dd>{clientState?.serviceWorker ?? 'wird geprüft …'}</dd>
        </div>
        <div>
          <dt>Subscription</dt>
          <dd>{clientState?.subscription ?? 'wird geprüft …'}</dd>
        </div>
      </dl>
      {testStatus === '' ? null : <p className="settings-hint">{testStatus}</p>}
      {error === '' ? null : <p className="form-error">{error}</p>}
      {events.length === 0 ? (
        <p className="settings-hint">Noch kein Push-Event in diesem Browser empfangen.</p>
      ) : (
        <ol className="push-debug-events">
          {events.slice(0, 8).map((event) => (
            <li key={event.id}>
              <strong>{statusLabel(event.status)}</strong>
              <time dateTime={event.receivedAt}>{formatTimestamp(event.receivedAt)}</time>
              {event.error === undefined ? null : <span>{event.error}</span>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

interface PushDebugPanelProps {
  serverNotificationsEnabled: boolean;
}

interface ClientPushState {
  permission: string;
  serviceWorker: string;
  subscription: string;
}

async function readClientPushState(): Promise<ClientPushState> {
  const permission = 'Notification' in window ? Notification.permission : 'nicht unterstützt';
  if (!('serviceWorker' in navigator)) {
    return { permission, serviceWorker: 'nicht unterstützt', subscription: 'nicht verfügbar' };
  }
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  return {
    permission,
    serviceWorker: registration.active?.state === 'activated' ? 'aktiv' : 'nicht aktiv',
    subscription: subscription === null ? 'nicht vorhanden' : 'vorhanden',
  };
}

function statusLabel(status: PushDebugEvent['status']): string {
  return {
    received: 'Push empfangen',
    parsed: 'Payload gelesen',
    displayed: 'Notification angezeigt',
    'parse-error': 'Payload-Fehler',
    'display-error': 'Anzeige-Fehler',
  }[status];
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'medium' });
}
